<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export interface MenuItem {
  id: string
  label: string
  danger?: boolean
  disabled?: boolean
}

defineProps<{ items: MenuItem[]; label?: string }>()
const { t } = useI18n()
const emit = defineEmits<{ select: [id: string] }>()

const open = ref(false)
const root = ref<HTMLElement | null>(null)

function onDocumentClick(event: MouseEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) open.value = false
}

watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('click', onDocumentClick)
  else document.removeEventListener('click', onDocumentClick)
})
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick))

function choose(id: string): void {
  open.value = false
  emit('select', id)
}
</script>

<template>
  <div ref="root" class="menu">
    <button
      type="button"
      class="ui-btn ui-btn-icon"
      :aria-expanded="open"
      :aria-label="label ?? t('menu.moreActions')"
      @click="open = !open"
    >
      ⋯
    </button>
    <ul v-if="open" class="dropdown" role="menu">
      <li v-for="item in items" :key="item.id">
        <button
          type="button"
          role="menuitem"
          :class="{ danger: item.danger }"
          :disabled="item.disabled"
          @click="choose(item.id)"
        >
          {{ item.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.menu {
  position: relative;
}

.dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 10;
  min-width: 12rem;
  margin: 0.25rem 0 0;
  padding: 0.3rem 0;
  list-style: none;
  border-radius: 12px;
  background: var(--tg-section-bg-color);
  border: 1px solid var(--tg-secondary-bg-color);
  box-shadow: 0 6px 24px rgb(0 0 0 / 18%);
}

.dropdown button {
  width: 100%;
  padding: 0.55rem 0.9rem;
  border: none;
  background: none;
  color: var(--tg-text-color);
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.dropdown button:disabled {
  color: var(--tg-hint-color);
  cursor: default;
}

.dropdown button.danger {
  color: var(--tg-destructive-text-color);
}
</style>
